import { useState } from 'react'
import {
  Alert,
  App as AntdApp,
  Button,
  Input,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import type { TableProps } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
  canEditKnowledge,
  canManageKnowledge,
  knowledgeProductDisplay,
  knowledgeProblemTypeColor,
  KNOWLEDGE_KIND_COLOR,
  KNOWLEDGE_KIND_OPTIONS,
  KNOWLEDGE_SOURCE_TYPE_COLOR,
  KNOWLEDGE_STATUS_COLOR,
  KNOWLEDGE_STATUS_LABEL,
  toFilterSelectOptions,
  withUnclassifiedProduct,
} from '@/modules/knowledge/constants'
import {
  useArchiveKnowledge,
  useKnowledgeList,
  useKnowledgeOverview,
  useKnowledgeProducts,
  useKnowledgeReviewReasonCodes,
  useKnowledgeSearch,
  usePublishKnowledge,
} from '@/modules/knowledge/queries'
import { useAuthStore } from '@/stores/authStore'
import ErrorResult from '@/components/feedback/ErrorResult'
import KnowledgeDetailDrawer from './components/KnowledgeDetailDrawer'
import KnowledgeEditModal from './components/KnowledgeEditModal'
import KnowledgeIndexStatusBar from './components/KnowledgeIndexStatusBar'
import KnowledgeProductPanel from './components/KnowledgeProductPanel'
import KnowledgeReviewNoteModal from './components/KnowledgeReviewNoteModal'
import type {
  KnowledgeEntry,
  KnowledgeKind,
  KnowledgeListParams,
  KnowledgeProblemType,
  KnowledgeSearchPayload,
  KnowledgeSourceType,
  KnowledgeStatus,
} from '@/types/knowledge'

const { Text } = Typography

// 语义检索返回 top_k 条，不分页
const SEMANTIC_TOP_K = 20
// Segmented 需要一个能承载「全部」的取值
const ALL = 'all'

function truncateContent(content: string, maxLen = 80): string {
  if (!content) return ''
  return content.length > maxLen ? content.slice(0, maxLen) + '...' : content
}

export default function KnowledgeListPage() {
  const { message } = AntdApp.useApp()
  const roles = useAuthStore((s) => s.roles)
  const canManage = canManageKnowledge(roles)
  const canEdit = canEditKnowledge(roles)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchMode, setSearchMode] = useState<'keyword' | 'semantic'>('keyword')
  // 关键词模式实时过滤列表；语义模式回车后才发起检索，避免逐字打请求
  const [keywordInput, setKeywordInput] = useState('')
  const [keywordFilter, setKeywordFilter] = useState('')
  const [semanticQuery, setSemanticQuery] = useState('')
  // 非 manager/admin 不传状态，由后端按「仅已发布」口径返回。
  // manager/admin 默认看全部状态：知识库以草稿为主（待审核），
  // 默认只看已发布会让页面直接空掉，产品面板点进去也什么都没有。
  const [status, setStatus] = useState<KnowledgeStatus | typeof ALL>(
    canManage ? ALL : 'published',
  )
  const [kind, setKind] = useState<KnowledgeKind | typeof ALL>(ALL)
  const [productKey, setProductKey] = useState<string | undefined>(undefined)
  const [problemType, setProblemType] = useState<KnowledgeProblemType | undefined>(undefined)
  const [sourceType, setSourceType] = useState<KnowledgeSourceType | undefined>(undefined)
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null)
  // 发布/退回都要留理由：AI 归纳条目发布时必填，退回时一律必填
  const [noteTarget, setNoteTarget] = useState<
    { entry: KnowledgeEntry; mode: 'publish' | 'archive' } | null
  >(null)

  // 非 manager/admin 不传状态，由后端按「仅已发布」口径返回
  const resolvedStatus = canManage ? (status === ALL ? undefined : status) : undefined
  const resolvedKind = kind === ALL ? undefined : kind

  // 产品维度面板：统计口径必须与列表一致，否则"智慧医疗 1886 条"点进去是空表
  const productsQuery = useKnowledgeProducts(resolvedStatus)
  // 人工录入的产品下拉需要**全量彩页清单**（与状态无关），不能跟着状态收窄，
  // 否则草稿为空时下拉会变成空选项。
  const productOptionsQuery = useKnowledgeProducts()
  const overviewQuery = useKnowledgeOverview(resolvedStatus)
  // 彩页产品总数：后端下发全量彩页清单（含 0 条知识的彩页），未归类项不计入
  const brochureTotal = (productOptionsQuery.data?.items || []).filter(
    (item) => item.product_key,
  ).length
  const publishMutation = usePublishKnowledge()
  const archiveMutation = useArchiveKnowledge()
  const reasonCodesQuery = useKnowledgeReviewReasonCodes()

  const listParams: KnowledgeListParams = {
    page,
    page_size: pageSize,
    keyword: keywordFilter || undefined,
    status: resolvedStatus,
    kind: resolvedKind,
    product_key: productKey,
    problem_type: problemType,
    source_type: sourceType,
  }

  const searchPayload: KnowledgeSearchPayload | null =
    searchMode === 'semantic' && semanticQuery
      ? {
          query: semanticQuery,
          top_k: SEMANTIC_TOP_K,
          kind: resolvedKind,
          product_key: productKey,
          problem_type: problemType,
          source_type: sourceType,
          status: resolvedStatus,
        }
      : null
  const semanticActive = !!searchPayload

  // 语义检索命中时不再拉列表，避免无意义请求
  const listQuery = useKnowledgeList(listParams, !semanticActive)
  const searchQuery = useKnowledgeSearch(searchPayload)

  const activeQuery = semanticActive ? searchQuery : listQuery
  // 列表项与检索项都是完整条目，详情抽屉直接渲染，无需再请求详情接口
  const items = (semanticActive ? searchQuery.data?.items : listQuery.data?.items) || []
  const selectedEntry =
    selectedEntryId === null ? null : items.find((item) => item.id === selectedEntryId) || null

  const resetPage = () => setPage(1)

  const handleModeChange = (value: 'keyword' | 'semantic') => {
    setSearchMode(value)
    resetPage()
    if (value === 'semantic') {
      setSemanticQuery(keywordInput.trim())
      setKeywordFilter('')
      return
    }
    setKeywordFilter(keywordInput.trim())
    setSemanticQuery('')
  }

  const handleKeywordChange = (value: string) => {
    setKeywordInput(value)
    if (searchMode === 'semantic') {
      // 清空输入即退出检索结果，回到列表
      if (!value.trim()) setSemanticQuery('')
      return
    }
    setKeywordFilter(value)
    resetPage()
  }

  const openCreate = () => {
    setEditingEntry(null)
    setEditOpen(true)
  }

  const openEdit = (entry: KnowledgeEntry) => {
    setEditingEntry(entry)
    setEditOpen(true)
    setSelectedEntryId(null)
  }

  const columns: NonNullable<TableProps<KnowledgeEntry>['columns']> = [
    { title: '标题', dataIndex: 'title', width: 240, ellipsis: true },
    {
      title: '产品', dataIndex: 'product_key', width: 200,
      render: (_: string, row: KnowledgeEntry) => (
        <Space size={4} wrap>
          {!row.product_key && <Tag color="default">未归属具体产品</Tag>}
          <span>{knowledgeProductDisplay(row.product_key, row.product_name, row.product_line)}</span>
        </Space>
      ),
    },
    {
      title: '知识形态', dataIndex: 'kind', width: 120,
      render: (value: KnowledgeKind, row: KnowledgeEntry) => (
        <Tag color={KNOWLEDGE_KIND_COLOR[value]}>{row.kind_label}</Tag>
      ),
    },
    {
      title: '问题类型', dataIndex: 'problem_type', width: 130,
      render: (value: string, row: KnowledgeEntry) =>
        row.problem_type_label ? (
          <Tag color={knowledgeProblemTypeColor(value)}>{row.problem_type_label}</Tag>
        ) : (
          <Text type="secondary">未分类</Text>
        ),
    },
    {
      title: '来源类型', dataIndex: 'source_type', width: 120,
      render: (value: KnowledgeSourceType, row: KnowledgeEntry) => (
        <Tag color={KNOWLEDGE_SOURCE_TYPE_COLOR[value]}>{row.source_type_label}</Tag>
      ),
    },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (value: KnowledgeStatus, row: KnowledgeEntry) => (
        <Tag color={KNOWLEDGE_STATUS_COLOR[value]}>{row.status_label}</Tag>
      ),
    },
    {
      title: '关键词', dataIndex: 'keywords', width: 150,
      render: (value: string[]) =>
        value?.length ? (
          <Space size={[0, 4]} wrap>
            {value.slice(0, 3).map((kw, index) => (
              <Tag key={index} color="blue">{kw}</Tag>
            ))}
          </Space>
        ) : (
          '-'
        ),
    },
    { title: '内容摘要', dataIndex: 'content', ellipsis: true, render: (value: string) => truncateContent(value) },
    {
      title: '更新时间', dataIndex: 'updated_at', width: 110,
      render: (value: string) => value?.slice(0, 10) || '-',
    },
  ]

  if (semanticActive) {
    columns.push({
      title: '相似度', dataIndex: 'score', width: 100,
      sorter: (a, b) => (a.score ?? -1) - (b.score ?? -1),
      render: (value: number | null | undefined) =>
        value === null || value === undefined ? <Text type="secondary">-</Text> : value.toFixed(4),
    })
  }

  columns.push({
    title: '操作', key: 'actions', width: 140, fixed: 'right',
    render: (_: unknown, row: KnowledgeEntry) => (
      <Space size="small">
        <Button type="link" size="small" onClick={() => setSelectedEntryId(row.id)}>
          详情
        </Button>
        {canManage && row.status === 'draft' && (
          <Button
            type="link"
            size="small"
            loading={publishMutation.isPending}
            onClick={() => setNoteTarget({ entry: row, mode: 'publish' })}
          >
            发布
          </Button>
        )}
        {canManage && row.status !== 'archived' && (
          <Button
            type="link"
            size="small"
            danger
            loading={archiveMutation.isPending}
            onClick={() => setNoteTarget({ entry: row, mode: 'archive' })}
          >
            {row.status === 'published' ? '下架' : '退回'}
          </Button>
        )}
      </Space>
    ),
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 索引状态条 */}
      <KnowledgeIndexStatusBar canManage={canManage} />

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* 产品维度浏览 */}
        <KnowledgeProductPanel
          products={productsQuery.data?.items || []}
          loading={productsQuery.isLoading}
          selectedProductKey={productKey}
          onSelect={(value) => {
            setProductKey(value)
            resetPage()
          }}
        />

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Space wrap>
            <Text type="secondary">检索模式：</Text>
            <Segmented
              value={searchMode}
              options={[
                { label: '关键词', value: 'keyword' },
                { label: '语义', value: 'semantic' },
              ]}
              onChange={(value) => handleModeChange(value as 'keyword' | 'semantic')}
            />
            <Input.Search
              placeholder={searchMode === 'semantic' ? '输入自然语言问题后回车检索' : '搜索知识标题/内容/关键词'}
              allowClear
              style={{ width: 300 }}
              value={keywordInput}
              onChange={(e) => handleKeywordChange(e.target.value)}
              onSearch={(value) => {
                if (searchMode !== 'semantic') return
                setSemanticQuery(value.trim())
              }}
            />
            <Segmented
              value={kind}
              options={[
                { label: '全部形态', value: ALL },
                ...KNOWLEDGE_KIND_OPTIONS,
              ]}
              onChange={(value) => {
                setKind(value as KnowledgeKind | typeof ALL)
                resetPage()
              }}
            />
            <Select
              allowClear
              placeholder="问题类型"
              style={{ width: 180 }}
              value={problemType}
              options={toFilterSelectOptions(overviewQuery.data?.filters.problem_types || [])}
              onChange={(value) => {
                setProblemType(value)
                resetPage()
              }}
            />
            <Select
              allowClear
              placeholder="来源类型"
              style={{ width: 170 }}
              value={sourceType}
              options={toFilterSelectOptions(overviewQuery.data?.filters.source_types || [])}
              onChange={(value) => {
                setSourceType(value)
                resetPage()
              }}
            />
            {canManage && (
              <Segmented
                value={status}
                options={[
                  { label: '全部', value: ALL },
                  { label: KNOWLEDGE_STATUS_LABEL.published, value: 'published' },
                  { label: KNOWLEDGE_STATUS_LABEL.draft, value: 'draft' },
                  { label: KNOWLEDGE_STATUS_LABEL.archived, value: 'archived' },
                ]}
                onChange={(value) => {
                  setStatus(value as KnowledgeStatus | typeof ALL)
                  resetPage()
                }}
              />
            )}
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                新增知识
              </Button>
            )}
          </Space>

          {/* 产品覆盖：已归属到彩页产品的产品数 / 彩页总数 */}
          {overviewQuery.data && brochureTotal > 0 && (
            <Text type="secondary">
              已覆盖彩页产品 {overviewQuery.data.summary.product_coverage}/{brochureTotal}
            </Text>
          )}

          {/* 降级提示：关键词模式，或语义模式但有腿失败时都要显式告知 */}
          {searchQuery.data?.degraded_reason && (
            <Alert
              type={searchQuery.data.mode === 'vector' ? 'info' : 'warning'}
              showIcon
              message={
                searchQuery.data.mode === 'vector'
                  ? '部分语义检索不可用，结果来自其余召回通道'
                  : '向量检索不可用，已降级为关键词匹配'
              }
              description={`降级原因：${searchQuery.data.degraded_reason}`}
            />
          )}

          {semanticActive && searchQuery.data && (
            <Space size="small">
              <Text type="secondary">检索模式：</Text>
              <Tag color={searchQuery.data.mode === 'vector' ? 'green' : 'orange'}>
                {searchQuery.data.mode === 'vector' ? '向量召回' : '关键词匹配'}
              </Tag>
              {searchQuery.data.recall.map((source) => (
                <Tag key={source} color="blue">
                  {source === 'ragflow' ? 'RAGFlow 知识库' : '自建向量库'}
                </Tag>
              ))}
              <Text type="secondary">命中 {searchQuery.data.items.length} 条</Text>
            </Space>
          )}

          {activeQuery.isError ? (
            <ErrorResult error={activeQuery.error} onRetry={() => activeQuery.refetch()} />
          ) : (
            <Table
              size="small"
              rowKey="id"
              loading={activeQuery.isLoading}
              dataSource={items}
              columns={columns}
              scroll={{ x: 1400 }}
              pagination={
                semanticActive
                  ? false
                  : {
                      current: page,
                      pageSize,
                      total: listQuery.data?.total || 0,
                      showSizeChanger: true,
                      showTotal: (total) => `共 ${total} 条`,
                      onChange: (nextPage, nextPageSize) => {
                        setPage(nextPage)
                        setPageSize(nextPageSize)
                      },
                    }
              }
            />
          )}
        </div>
      </div>

      {/* 详情抽屉 */}
      <KnowledgeDetailDrawer
        entry={selectedEntry}
        onClose={() => setSelectedEntryId(null)}
        onEdit={openEdit}
      />

      {/* 发布 / 退回理由 */}
      <KnowledgeReviewNoteModal
        open={!!noteTarget}
        title={
          noteTarget?.mode === 'publish'
            ? '发布知识条目'
            : noteTarget?.entry.status === 'published'
              ? '下架知识条目'
              : '退回知识条目'
        }
        description={
          noteTarget?.mode === 'publish'
            ? noteTarget.entry.ai_generated
              ? '这条是 AI 归纳内容，必须填写审核说明后才能发布。'
              : '发布后该条目会进入客服问答库。'
            : '请说明理由 —— 事后要能查到这条为什么被拿掉。'
        }
        placeholder={
          noteTarget?.mode === 'publish' ? '例如：已核对，内容准确' : '例如：来源侧没有解决方案，价值低'
        }
        required={noteTarget?.mode === 'archive' || !!noteTarget?.entry.ai_generated}
        confirmText={noteTarget?.mode === 'publish' ? '发布' : '确定'}
        danger={noteTarget?.mode === 'archive'}
        loading={publishMutation.isPending || archiveMutation.isPending}
        // 理由标签由后端下发，一次点击即可 —— 顺带把"标签治理"覆盖到列表页
        reasonOptions={(reasonCodesQuery.data?.by_action?.[noteTarget?.mode ?? 'publish'] ?? []).map(
          (code) => ({ value: code, label: reasonCodesQuery.data?.labels?.[code] ?? code }),
        )}
        onCancel={() => setNoteTarget(null)}
        onSubmit={({ note, reasonCode }) => {
          if (!noteTarget) return
          const { entry, mode } = noteTarget
          const done = () => {
            setNoteTarget(null)
            message.success(mode === 'publish' ? '发布成功' : '已归档')
          }
          const failed = (err: unknown) =>
            message.error(err instanceof Error ? err.message : '操作失败，当前角色可能无此权限')
          if (mode === 'publish') {
            publishMutation.mutate(
              { entryId: entry.id, note: note || undefined, reasonCode },
              { onSuccess: done, onError: failed },
            )
          } else {
            archiveMutation.mutate(
              { entryId: entry.id, note: note || undefined, reasonCode },
              { onSuccess: done, onError: failed },
            )
          }
        }}
      />

      {/* 新增 / 编辑 */}
      <KnowledgeEditModal
        open={editOpen}
        entry={editingEntry}
        productOptions={withUnclassifiedProduct(productOptionsQuery.data?.items || [])}
        canPublishDirectly={canManage}
        onClose={() => {
          setEditOpen(false)
          setEditingEntry(null)
        }}
      />
    </div>
  )
}
